import { useEffect, useMemo, useState } from "react";
import {
  useGetAllPermissionsQuery,
  useGetRoleDetailsQuery,
  useRevokeRolePermissionsMutation,
  useSyncRolePermissionsMutation,
} from "../api/accessApi";

type Props = { roleId: number };

function errMsg(e: any) {
  return e?.data?.message || e?.error || "Request failed";
}

export default function RolePrivilegesEditor({ roleId }: Props) {
  const { data: allPerms = {}, isLoading: loadingAll } =
    useGetAllPermissionsQuery();
  const {
    data: role,
    isLoading: loadingRole,
    refetch,
  } = useGetRoleDetailsQuery(roleId);

  const [updatePerms, { isLoading: saving }] = useSyncRolePermissionsMutation();
  const [revokeAll, { isLoading: revoking }] =
    useRevokeRolePermissionsMutation();

  const current = useMemo(() => {
    return new Set((role?.permissions || []).map((p) => p.name));
  }, [role]);

  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [msg, setMsg] = useState<{
    type: "success" | "error";
    text: string;
  } | null>(null);

  useEffect(() => {
    setSelected(new Set(Array.from(current)));
  }, [roleId, role?.permissions?.length]);

  const toggle = (name: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      next.has(name) ? next.delete(name) : next.add(name);
      return next;
    });
  };

  const onSave = async () => {
    setMsg(null);
    try {
      await updatePerms({ roleId, permissions: Array.from(selected) }).unwrap();
      setMsg({ type: "success", text: "Privileges updated successfully" });
      refetch();
    } catch (e: any) {
      setMsg({ type: "error", text: errMsg(e) });
    }
  };

  const onRevoke = async () => {
    const ok = window.confirm("Revoke ALL permissions for this role?");
    if (!ok) return;

    setMsg(null);
    try {
      await revokeAll({ roleId }).unwrap();
      setMsg({ type: "success", text: "All permissions revoked" });
      refetch();
    } catch (e: any) {
      setMsg({ type: "error", text: errMsg(e) });
    }
  };

  const busy = saving || revoking;

  if (loadingAll || loadingRole)
    return <div className="text-sm text-gray-600">Loading privileges...</div>;

  return (
    <div className="border rounded-xl p-4">
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="font-semibold text-gray-800">Role Privileges</div>
          <div className="text-sm text-gray-600 mt-1">
            Role: <span className="font-semibold">{role?.name}</span>
          </div>
        </div>

        <div className="flex gap-2">
          <button
            onClick={onRevoke}
            disabled={busy}
            className="px-3 py-1.5 rounded-lg border text-red-600 hover:bg-red-50 text-xs disabled:opacity-60"
          >
            {revoking ? "Revoking..." : "Revoke All"}
          </button>

          <button
            onClick={onSave}
            disabled={busy}
            className="px-3 py-1.5 rounded-lg bg-gray-900 text-white hover:bg-gray-800 text-xs disabled:opacity-60 inline-flex items-center gap-2"
          >
            {saving ? (
              <>
                <span className="h-3.5 w-3.5 rounded-full border-2 border-white border-t-transparent animate-spin" />
                Saving...
              </>
            ) : (
              "Save Changes"
            )}
          </button>
        </div>
      </div>

      {msg ? (
        <div
          className={`mt-3 rounded-lg border p-2 text-sm ${
            msg.type === "success"
              ? "bg-green-50 border-green-200 text-green-800"
              : "bg-red-50 border-red-200 text-red-800"
          }`}
        >
          {msg.text}
        </div>
      ) : null}

      <div className="mt-4 space-y-4">
        {Object.entries(allPerms).map(([module, perms]) => (
          <div key={module} className="border rounded-xl p-3">
            <div className="font-semibold text-gray-800 capitalize">
              {module}
            </div>

            <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
              {perms.map((p) => (
                <label
                  key={p.id}
                  className="flex items-center gap-2 rounded-lg border px-3 py-2 hover:bg-gray-50 cursor-pointer"
                >
                  <input
                    type="checkbox"
                    checked={selected.has(p.name)}
                    onChange={() => toggle(p.name)}
                    disabled={busy}
                  />
                  <div className="text-sm text-gray-700">
                    {p.name}
                    <div className="text-xs text-gray-500">{p.action}</div>
                  </div>
                </label>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
